/**
 * Fixes variant availability when Maat and Framemaat share values (L/L, XL/XL).
 * Keeps hidden Framemaat inputs in sync with Maat so the theme can resolve variants.
 */

const PRODUCT_SELECTOR = 'xo-product[xo-product-id]';
const RADIO_SELECTOR = 'input[type="radio"]:not([xo-for-quick-view])';
const MAAT_NAMES = ['Maat', 'Size'];
const FRAME_NAMES = ['Framemaat', 'Frame size', 'Frame Size'];

function parseFieldName(name, optionNames) {
  const signalMatch = name.match(/^\[SIGNAL_\d+_(\d+)\]/);
  const optionName = name.replace(/^\[[^\]]*\]/, '');

  if (signalMatch) {
    return {
      optionIndex: parseInt(signalMatch[1], 10),
      optionName,
    };
  }

  const optionIndex = optionNames.indexOf(optionName);
  return {
    optionIndex: optionIndex >= 0 ? optionIndex : null,
    optionName,
  };
}

function getProductData(productEl) {
  const script = productEl.querySelector('xo-product-data script[type="application/json"]');
  if (!script) return null;

  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

function getOptionNames(productData) {
  return (productData.options || []).filter((name) => name && name !== 'Title');
}

function getMirroredMaatFramemaat(optionNames, variants) {
  const maatIndex = optionNames.findIndex((name) => MAAT_NAMES.includes(name));
  const framemaatIndex = optionNames.findIndex((name) => FRAME_NAMES.includes(name));

  if (maatIndex < 0 || framemaatIndex < 0) return null;

  const mirrors = variants.every(
    (variant) => variant.options[maatIndex] === variant.options[framemaatIndex]
  );

  if (!mirrors) return null;

  return { maatIndex, framemaatIndex };
}

function hasOverlappingOptionValues(variants, optionNames) {
  if (optionNames.length < 2) return false;

  const valuesByOption = optionNames.map((_, index) => {
    const values = new Set();
    variants.forEach((variant) => {
      const value = variant.options?.[index];
      if (value) values.add(value);
    });
    return values;
  });

  for (let i = 0; i < valuesByOption.length; i += 1) {
    for (let j = i + 1; j < valuesByOption.length; j += 1) {
      for (const value of valuesByOption[i]) {
        if (valuesByOption[j].has(value)) return true;
      }
    }
  }

  return false;
}

function getRadiosForOption(productEl, optionNames, optionIndex) {
  return Array.from(productEl.querySelectorAll(RADIO_SELECTOR)).filter((radio) => {
    const parsed = parseFieldName(radio.name, optionNames);
    const index = parsed.optionIndex ?? optionNames.indexOf(parsed.optionName);
    return index === optionIndex;
  });
}

function getVisibleOptionIndices(productEl, optionNames) {
  return optionNames
    .map((_, index) => index)
    .filter((index) => {
      const radios = getRadiosForOption(productEl, optionNames, index);
      return radios.some((radio) => !radio.closest('[hidden]'));
    });
}

function isVariantInStock(variant) {
  if (variant.available === true) return true;
  if (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0) return true;
  if (variant.inventory_policy === 'continue') return true;
  return false;
}

function getSelectedValues(productEl, optionNames) {
  return optionNames.map((_, index) => {
    const checked = getRadiosForOption(productEl, optionNames, index).find((radio) => radio.checked);
    return checked ? checked.value : null;
  });
}

function buildHypothesis(selectedValues, optionIndex, optionValue, mirrored) {
  const hypothesis = [...selectedValues];
  hypothesis[optionIndex] = optionValue;

  if (mirrored && optionIndex === mirrored.maatIndex) {
    hypothesis[mirrored.framemaatIndex] = optionValue;
  }

  return hypothesis;
}

function valuesMatchVariant(variant, values) {
  return variant.options.every((optionValue, index) => {
    const selected = values[index];
    if (selected == null || selected === '') return true;
    return optionValue === selected;
  });
}

function isValueAvailable(variants, values) {
  return variants.some((variant) => valuesMatchVariant(variant, values) && isVariantInStock(variant));
}

function isValueExisting(variants, values) {
  return variants.some((variant) => valuesMatchVariant(variant, values));
}

function resolveVariant(variants, values) {
  return (
    variants.find((variant) =>
      variant.options.every((optionValue, index) => optionValue === values[index])
    ) ||
    variants.find((variant) => isVariantInStock(variant) && valuesMatchVariant(variant, values)) ||
    variants.find((variant) => valuesMatchVariant(variant, values)) ||
    null
  );
}

function updateHiddenVariantInput(productEl, variant) {
  if (!variant) return;

  const form =
    productEl.querySelector('form[action*="/cart/add"]') ||
    document.querySelector('form[action*="/cart/add"]:not([xo-buy-ignore])');

  const input = form?.querySelector('input[name="id"]');
  if (input) input.value = String(variant.id);
}

function syncMirroredFramemaat(productEl, optionNames, mirrored) {
  if (!mirrored) return;

  const maatValue = getSelectedValues(productEl, optionNames)[mirrored.maatIndex];
  if (!maatValue) return;

  const framemaatRadios = getRadiosForOption(productEl, optionNames, mirrored.framemaatIndex);
  const target = framemaatRadios.find((radio) => radio.value === maatValue);
  if (!target || target.checked) return;

  target.checked = true;
  target.dispatchEvent(new Event('change', { bubbles: true }));
}

function setRadioState(radio, available, existing) {
  if (available) {
    radio.removeAttribute('xo-disabled');
  } else {
    radio.setAttribute('xo-disabled', '');
  }

  if (existing) {
    radio.removeAttribute('xo-unavailable');
  } else {
    radio.setAttribute('xo-unavailable', '');
  }
}

function applyAvailability(productEl, optionNames, variants, mirrored) {
  syncMirroredFramemaat(productEl, optionNames, mirrored);

  const selectedValues = getSelectedValues(productEl, optionNames);
  const visibleIndices = getVisibleOptionIndices(productEl, optionNames);

  visibleIndices.forEach((optionIndex) => {
    getRadiosForOption(productEl, optionNames, optionIndex)
      .filter((radio) => !radio.closest('[hidden]'))
      .forEach((radio) => {
        const hypothesis = buildHypothesis(selectedValues, optionIndex, radio.value, mirrored);
        setRadioState(
          radio,
          isValueAvailable(variants, hypothesis),
          isValueExisting(variants, hypothesis)
        );
      });
  });

  updateHiddenVariantInput(productEl, resolveVariant(variants, getSelectedValues(productEl, optionNames)));
}

function runAfterTheme(fn) {
  requestAnimationFrame(() => {
    fn();
    window.setTimeout(fn, 50);
    window.setTimeout(fn, 200);
  });
}

function createProductFix(productEl, productData) {
  const optionNames = getOptionNames(productData);
  const variants = productData.variants || [];
  const mirrored = getMirroredMaatFramemaat(optionNames, variants);

  if (!hasOverlappingOptionValues(variants, optionNames) && !mirrored) {
    return null;
  }

  const run = () => applyAvailability(productEl, optionNames, variants, mirrored);

  const onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!productEl.contains(target)) return;

    runAfterTheme(run);
  };

  productEl.addEventListener('change', onChange);
  runAfterTheme(run);

  return () => {
    productEl.removeEventListener('change', onChange);
  };
}

const productFixes = new WeakMap();

function initProductFix(productEl) {
  if (productFixes.has(productEl)) return;

  const productData = getProductData(productEl);
  if (!productData?.variants?.length) return;

  const cleanup = createProductFix(productEl, productData);
  if (cleanup) productFixes.set(productEl, cleanup);
}

function initVariantAvailabilityFix() {
  document.querySelectorAll(PRODUCT_SELECTOR).forEach(initProductFix);

  const rootObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(PRODUCT_SELECTOR)) initProductFix(node);
        node.querySelectorAll(PRODUCT_SELECTOR).forEach(initProductFix);
      });
    });
  });

  rootObserver.observe(document.body, { childList: true, subtree: true });
}

export default initVariantAvailabilityFix;
